// src/pages/Dashboard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useNavigate, useParams } from "react-router-dom";

import { DollarIcon } from "../icons/DollarIcon";
import { PurchaseIcon } from "../icons/PurchaseIcon";
import { ReceivableIcon } from "../icons/ReceivableIcon";
import { PayableIcon } from "../icons/PayableIcon";
import { CustomerIcon } from "../icons/CustomerIcon";
import { InventoryIcon } from "../icons/InventoryIcon";
import { ReportIcon } from "../icons/ReportIcon";
import { SettingsIcon } from "../icons/SettingsIcon";

import {
  ReceiptPercentIcon,
  ClipboardDocumentListIcon,
  BuildingStorefrontIcon,
  DocumentTextIcon,
  BookOpenIcon,
  PencilSquareIcon,
  ArrowsRightLeftIcon,
  CameraIcon,
} from "@heroicons/react/24/outline";

import { apiGet, apiPost } from "../api/client";
import AccountMenu from "../components/AccountMenu";
import { AuthMe } from "../types/auth";
import { formatMoney as formatMoneyShared } from "../utils/formatNumber";

import { usePostHog } from "@posthog/react";

const TITLE_COLOR = "#1f1f1f";

// ---- helpers ----
function companyRoute(company_id: string | undefined, path: string): string {
  if (!company_id) return path;
  return `/company/${company_id}${path.startsWith("/") ? path : "/" + path}`;
}

// format local date as YYYY-MM-DD (no UTC shift)
function formatDateYMD(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

// Monday-start week
function startOfWeekMonday(today: Date): Date {
  const d = startOfLocalDay(today);
  const day = d.getDay(); // 0 Sun, 1 Mon, ...
  const diff = (day + 6) % 7; // Mon => 0, Tue => 1, Sun => 6
  return addDays(d, -diff);
}

function parseYearEndMMDD(
  value: string | null | undefined,
): { mm: number; dd: number } | null {
  if (!value) return null;
  const m = value.trim().match(/(\d{1,2})\D+(\d{1,2})/);
  if (!m) return null;
  const mm = Number(m[1]);
  const dd = Number(m[2]);
  if (!Number.isFinite(mm) || !Number.isFinite(dd)) return null;
  if (mm < 1 || mm > 12) return null;
  if (dd < 1 || dd > 31) return null;
  return { mm, dd };
}

// FY start = day AFTER the most recent year-end
function financialYearStart(today: Date, yearEndMMDD: string): Date {
  const parsed = parseYearEndMMDD(yearEndMMDD) ?? { mm: 12, dd: 31 };
  const t = startOfLocalDay(today);

  const y = t.getFullYear();
  const yearEndThisYear = new Date(y, parsed.mm - 1, parsed.dd);
  const yearEndPrevYear = new Date(y - 1, parsed.mm - 1, parsed.dd);

  const lastYearEnd =
    t.getTime() <= yearEndThisYear.getTime()
      ? yearEndPrevYear
      : yearEndThisYear;
  return addDays(startOfLocalDay(lastYearEnd), 1);
}

function daysBetweenInclusive(start: Date, end: Date): number {
  const s = startOfLocalDay(start).getTime();
  const e = startOfLocalDay(end).getTime();
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.max(1, Math.floor((e - s) / msPerDay) + 1);
}

type QuickLink = {
  label: string;
  searchAlias: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  route: string;
  staffAllowed?: boolean;
};

// English aliases are search-only; routes and staffAllowed remain semantic identity.
const getQuickLinks = (t: TFunction): QuickLink[] => [
  {
    label: t("dashboard.links.sales"), searchAlias: "Sales",
    icon: ReceiptPercentIcon,
    route: "/sales",
    staffAllowed: true,
  },
  {
    label: t("dashboard.links.scanner"), searchAlias: "Scanner",
    icon: CameraIcon,
    route: "/scanner",
  },
  { label: t("dashboard.links.purchases"), searchAlias: "Purchases", icon: ClipboardDocumentListIcon, route: "/purchases" },
  { label: t("dashboard.links.spending"), searchAlias: "Spending", icon: DocumentTextIcon, route: "/recognition" },
  { label: t("dashboard.links.receivables"), searchAlias: "Receivables", icon: ReceivableIcon, route: "/receivables" },
  { label: t("dashboard.links.payables"), searchAlias: "Payables", icon: PayableIcon, route: "/payables" },
  {
    label: t("dashboard.links.customers"), searchAlias: "Customers",
    icon: CustomerIcon,
    route: "/customers",
    staffAllowed: true,
  },
  { label: t("dashboard.links.vendors"), searchAlias: "Vendors", icon: BuildingStorefrontIcon, route: "/vendors" },
  {
    label: t("dashboard.links.products"), searchAlias: "Products",
    icon: InventoryIcon,
    route: "/products",
    staffAllowed: true,
  },
  {
    label: t("dashboard.links.enterAdvanceWithdrawal"), searchAlias: "Enter Advance/Withdrawal",
    icon: ArrowsRightLeftIcon,
    route: "/owner-funds/new",
  },
  {
    label: t("dashboard.links.advanceWithdrawal"), searchAlias: "Advance/Withdrawal",
    icon: DollarIcon,
    route: "/owner-funds",
  },
  { label: t("dashboard.links.reports"), searchAlias: "Reports", icon: ReportIcon, route: "/reports" },
  { label: t("dashboard.links.numbers"), searchAlias: "Numbers", icon: BookOpenIcon, route: "/books" },
  { label: t("dashboard.links.settings"), searchAlias: "Settings", icon: SettingsIcon, route: "/settings" },
];

type CompanySetting = { key: string; value: string };

// Match IncomeStatement.tsx shape
type AccountSum = { name: string; total: number };
type Section = { accounts: AccountSum[]; total: number };

type IncomeStatementSummary = {
  income: Section;
  cogs: Section;
  operating_expense: Section;
  other_income: Section;
  other_expense: Section;
  net_income?: number;
};

// HK corporate tax helper (8.25% first 2M, 16.5% thereafter)
function estimateHongKongCorporateTax(annualProfit: number): number {
  if (annualProfit <= 0) return 0;
  const bandLimit = 2_000_000;
  const firstBand = Math.min(annualProfit, bandLimit);
  const secondBand = Math.max(annualProfit - bandLimit, 0);
  return firstBand * 0.0825 + secondBand * 0.165;
}

type BizRange = "week" | "month" | "ytd";

function getBizRangeStart(range: BizRange, today: Date): Date {
  const t = startOfLocalDay(today);
  if (range === "week") return startOfWeekMonday(t);
  if (range === "month") return new Date(t.getFullYear(), t.getMonth(), 1);
  return new Date(t.getFullYear(), 0, 1);
}

function rangeTitle(range: BizRange, t: TFunction): string {
  if (range === "week") return t("dashboard.business.thisWeek");
  if (range === "month") return t("dashboard.business.thisMonth");
  return t("dashboard.business.yearToDate");
}

function formatMoney(n: number): string {
  return formatMoneyShared(n, "$", { minDp: 0, maxDp: 0 });
}

function deriveMetrics(summary: IncomeStatementSummary | null) {
  const sales =
    summary && typeof summary.income?.total === "number"
      ? summary.income.total
      : null;
  const cogs =
    summary && typeof summary.cogs?.total === "number"
      ? summary.cogs.total
      : null;

  const opExp =
    summary && typeof summary.operating_expense?.total === "number"
      ? summary.operating_expense.total
      : 0;

  const otherInc =
    summary && typeof summary.other_income?.total === "number"
      ? summary.other_income.total
      : 0;
  const otherExp =
    summary && typeof summary.other_expense?.total === "number"
      ? summary.other_expense.total
      : 0;

  let profit: number | null = null;
  if (summary) {
    if (typeof summary.net_income === "number") {
      profit = summary.net_income;
    } else if (sales !== null && cogs !== null) {
      profit = sales - cogs - opExp + otherInc - otherExp;
    }
  }

  const costs = cogs === null ? null : Math.max(0, cogs + opExp + otherExp);

  const profitMargin =
    sales !== null && profit !== null && sales !== 0
      ? (profit / sales) * 100
      : null;
  const keepPerDollar = profitMargin !== null ? Math.round(profitMargin) : null;

  return { sales, costs, profit, profitMargin, keepPerDollar };
}

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id?: string }>();

  const [companyName, setCompanyName] = useState("");
  const [search, setSearch] = useState("");
  const [user, setUser] = useState<AuthMe | null>(null);

  const [bizRange, setBizRange] = useState<BizRange>("month");
  const [bizSummary, setBizSummary] = useState<IncomeStatementSummary | null>(
    null,
  );
  const [fySummary, setFySummary] = useState<IncomeStatementSummary | null>(
    null,
  );

  const [yearEndMMDD, setYearEndMMDD] = useState("12-31");

  const isCompanyAdmin = user?.is_company_admin === true;
  const isSuperAdmin = user?.is_ricebook_admin === true;
  const canViewAdminDashboard = isCompanyAdmin || isSuperAdmin;
  const isStaffPOS = !!user && !canViewAdminDashboard;

  useEffect(() => {
    apiGet<AuthMe>("/auth/me")
      .then((u) => {
        setUser(u);
        setCompanyName(u?.company_name || "");
      })
      .catch(() => {
        setUser(null);
        setCompanyName("");
      });
  }, []);

  useEffect(() => {
    if (!company_id || !canViewAdminDashboard) {
      setYearEndMMDD("12-31");
      return;
    }

    apiGet<CompanySetting>(
      `/settings/financial-year-end?company_id=${company_id}`,
    )
      .then((res) => setYearEndMMDD((res?.value || "").trim() || "12-31"))
      .catch(() => setYearEndMMDD("12-31"));
  }, [company_id, canViewAdminDashboard]);

  useEffect(() => {
    if (!company_id || !canViewAdminDashboard) {
      setBizSummary(null);
      return;
    }

    const today = new Date();
    const end = formatDateYMD(today);
    const start = formatDateYMD(getBizRangeStart(bizRange, today));

    apiGet<IncomeStatementSummary>(
      `/reports/income-statement?start=${start}&end=${end}`,
    )
      .then(setBizSummary)
      .catch(() => setBizSummary(null));
  }, [company_id, bizRange, canViewAdminDashboard]);

  useEffect(() => {
    if (!company_id || !canViewAdminDashboard) {
      setFySummary(null);
      return;
    }

    const today = new Date();
    const end = formatDateYMD(today);
    const fyStart = financialYearStart(today, yearEndMMDD);
    const start = formatDateYMD(fyStart);

    apiGet<IncomeStatementSummary>(
      `/reports/income-statement?start=${start}&end=${end}`,
    )
      .then(setFySummary)
      .catch(() => setFySummary(null));
  }, [company_id, yearEndMMDD, canViewAdminDashboard]);

  const biz = useMemo(() => deriveMetrics(bizSummary), [bizSummary]);
  const fy = useMemo(() => deriveMetrics(fySummary), [fySummary]);

  const fyStart = useMemo(
    () => financialYearStart(new Date(), yearEndMMDD),
    [yearEndMMDD],
  );
  const fyElapsedDays = useMemo(
    () => daysBetweenInclusive(fyStart, new Date()),
    [fyStart],
  );

  const hasPositiveFYProfit = typeof fy.profit === "number" && fy.profit > 0;

  const fyAnnualProfitEstimate = hasPositiveFYProfit
    ? (fy.profit! / fyElapsedDays) * 365
    : 0;
  const fyAnnualTaxEstimate = estimateHongKongCorporateTax(
    fyAnnualProfitEstimate,
  );
  const fyEffectiveTaxRate =
    fyAnnualProfitEstimate > 0
      ? fyAnnualTaxEstimate / fyAnnualProfitEstimate
      : null;
  const fyTaxPot =
    hasPositiveFYProfit && fyEffectiveTaxRate !== null
      ? fy.profit! * fyEffectiveTaxRate
      : 0;

  async function handleAvatarChange(style: string, seed: string) {
    try {
      await apiPost("/auth/update-profile", {
        avatar_style: style,
        avatar_seed: seed,
      });
      setUser((prev) =>
        prev ? { ...prev, avatar_style: style, avatar_seed: seed } : prev,
      );
    } catch {
      alert(t("dashboard.errors.avatarUpdateFailed"));
    }
  }

  const posthog = usePostHog();

  const handleLogout = () => {
    posthog.reset(); // ✅ ADD THIS
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
    window.location.href = "/login";
  };

  const filteredLinks = getQuickLinks(t)
    .filter((link) =>
      [link.label, link.searchAlias].some((label) =>
        label.toLowerCase().includes(search.toLowerCase()),
      ),
    )
    .filter((link) => {
      if (!user) return true;
      if (!isStaffPOS) return true;
      return !!link.staffAllowed;
    });

  const pressTile =
    "hover:shadow-md active:scale-95 active:translate-y-px active:shadow-sm " +
    "transition-[transform,box-shadow] " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:ring-offset-2 focus:ring-offset-gray-50";

  return (
    <div className="max-w-6xl mx-auto px-4 pb-16 pt-6 text-[13px] font-normal text-gray-700">
      <div className="sticky top-0 z-20 bg-gray-50 flex flex-col sm:flex-row justify-between items-center pb-3 mb-2 gap-2 border-b border-gray-100">
        <div
          className="text-[18px] md:text-[20px] font-semibold tracking-tight"
          style={{ color: TITLE_COLOR }}
        >
          {companyName.trim() ? companyName : t("dashboard.companyFallback")}
        </div>

        <div className="flex gap-4 items-center w-full sm:w-auto">
          <div className="relative w-full max-w-xs">
            <span className="absolute left-3 top-2.5 text-gray-400 pointer-events-none">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  d="M20 20l-3-3"
                />
              </svg>
            </span>
            <input
              type="text"
              placeholder={t("dashboard.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-gray-200 bg-white placeholder-gray-400 text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue transition text-[13px]"
            />
          </div>

          <span className="ml-2 text-gray-400 text-xl flex items-center h-9">
            🔔
          </span>

          <div className="flex items-center h-9">
            {user ? (
              <AccountMenu
                user={user}
                onAvatarChange={handleAvatarChange}
                onLogout={handleLogout}
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse" />
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-10">
        <div className="grid grid-cols-1 gap-4">
          <button
            className={[
              "flex flex-col items-center justify-center bg-white rounded-3xl shadow-sm border border-gray-100 w-full",
              "px-4 py-6 sm:px-12 sm:py-8",
              pressTile,
            ].join(" ")}
            onClick={() => navigate(companyRoute(company_id, "/sales/new"))}
          >
            <DollarIcon className="w-14 h-14 text-appleblue mb-4" />
            <div className="flex flex-col items-center">
              <span
                className="text-[15px] font-semibold tracking-tight"
                style={{ color: TITLE_COLOR }}
              >
                {t("dashboard.actions.enterSale")}
              </span>
              <span className="text-gray-400 mt-1 text-[13px] font-normal text-center">
                {t("dashboard.actions.createSaleDescription")}
              </span>
            </div>
          </button>

          {canViewAdminDashboard && (
            <button
              className={[
                "flex flex-col items-center justify-center bg-white rounded-3xl shadow-sm border border-gray-100 w-full",
                "px-4 py-6 sm:px-12 sm:py-8",
                pressTile,
              ].join(" ")}
              onClick={() => navigate(companyRoute(company_id, "/scanner"))}
            >
              <CameraIcon className="w-14 h-14 text-appleblue mb-4" />
              <div className="flex flex-col items-center">
                <span
                  className="text-[15px] font-semibold tracking-tight"
                  style={{ color: TITLE_COLOR }}
                >
                  {t("dashboard.actions.scanner")}
                </span>
                <span className="text-gray-400 mt-1 text-[13px] font-normal text-center">
                  {t("dashboard.actions.scanDescription")}
                </span>
              </div>
            </button>
          )}
        </div>

        {canViewAdminDashboard && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
            <button
              className={[
                "flex flex-col items-center bg-white rounded-2xl shadow-sm border border-gray-100 w-full p-6 group",
                pressTile,
              ].join(" ")}
              onClick={() =>
                navigate(companyRoute(company_id, "/purchases/new"))
              }
            >
              <PurchaseIcon className="w-10 h-10 text-appleblue mb-3 group-hover:text-appleblueD group-active:text-appleblueD transition" />
              <div className="flex flex-col items-center">
                <span
                  className="text-[14px] font-semibold"
                  style={{ color: TITLE_COLOR }}
                >
                  {t("dashboard.actions.enterPurchase")}
                </span>
                <span className="text-gray-400 mt-1 text-[13px] font-normal text-center">
                  {t("dashboard.actions.purchaseDescription")}
                </span>
              </div>
            </button>

            <button
              className={[
                "flex flex-col items-center bg-white rounded-2xl shadow-sm border border-gray-100 w-full p-6 group",
                pressTile,
              ].join(" ")}
              onClick={() =>
                navigate(companyRoute(company_id, "/expenses/new"))
              }
            >
              <PencilSquareIcon className="w-10 h-10 text-appleblue mb-3 group-hover:text-appleblueD group-active:text-appleblueD transition" />
              <div className="flex flex-col items-center">
                <span
                  className="text-[14px] font-semibold"
                  style={{ color: TITLE_COLOR }}
                >
                  {t("dashboard.actions.enterExpense")}
                </span>
                <span className="text-gray-400 mt-1 text-[13px] font-normal text-center">
                  {t("dashboard.actions.expenseDescription")}
                </span>
              </div>
            </button>
          </div>
        )}

        {canViewAdminDashboard && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm px-5 py-5 flex flex-col">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-3 min-h-[40px]">
                <div className="flex items-center min-w-0 w-full sm:w-auto">
                  <ReportIcon className="w-6 h-6 text-appleblue mr-2 shrink-0" />
                  <span className="text-[15px] font-semibold text-gray-700">
                    {rangeTitle(bizRange, t)}
                  </span>
                </div>

                <div className="flex justify-end sm:shrink-0">
                  <div className="grid grid-cols-3 w-full sm:inline-flex sm:w-auto rounded-xl border border-gray-200 bg-gray-50 p-1">
                    {(
                      [
                        { id: "week", label: t("dashboard.ranges.week") },
                        { id: "month", label: t("dashboard.ranges.month") },
                        { id: "ytd", label: t("dashboard.ranges.ytd") },
                      ] as const
                    ).map((opt) => {
                      const active = bizRange === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setBizRange(opt.id)}
                          className={[
                            "px-3 py-1.5 text-xs font-semibold rounded-lg transition",
                            "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                            active
                              ? "bg-white shadow-sm text-gray-900"
                              : "text-gray-500 hover:text-gray-700",
                          ].join(" ")}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs text-gray-400">{t("dashboard.metrics.sales")}</div>
                  <div
                    className="text-base font-semibold tabular-nums"
                    style={{ color: TITLE_COLOR }}
                  >
                    {biz.sales === null ? "--" : formatMoney(biz.sales)}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-400">{t("dashboard.metrics.costs")}</div>
                  <div
                    className="text-base font-semibold tabular-nums"
                    style={{ color: TITLE_COLOR }}
                  >
                    {biz.costs === null ? "--" : formatMoney(biz.costs)}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-400">{t("dashboard.metrics.netProfit")}</div>
                  <div
                    className="text-base font-semibold tabular-nums"
                    style={{ color: TITLE_COLOR }}
                  >
                    {biz.profit === null ? "--" : formatMoney(biz.profit)}
                  </div>
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-500 text-center">
                {biz.profitMargin === null ? (
                  t("dashboard.business.marginUnavailable")
                ) : (
                  <Trans
                    defaults={t("dashboard.business.marginSummary", {
                      margin: `<margin>${biz.profitMargin.toFixed(1)}</margin>`,
                      cents: `<cents>${biz.keepPerDollar}</cents>`,
                    }).replace("</margin>%", "%</margin>").replace("</cents>¢", "¢</cents>")}
                    components={{
                      margin: <span className="font-semibold text-gray-700" />,
                      cents: <span className="font-semibold text-gray-700" />,
                    }}
                  />
                )}
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm px-5 py-5 flex flex-col">
              <div className="flex items-center justify-between mb-3 gap-3 min-h-[40px]">
                <div className="flex items-center min-w-0 w-full sm:w-auto">
                  <DollarIcon className="w-6 h-6 text-appleblue mr-2 shrink-0" />
                  <span className="text-[15px] font-semibold text-gray-700">
                    {t("dashboard.financialYear.title")}
                  </span>
                </div>

                <div className="hidden sm:flex justify-end">
                  <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1 invisible pointer-events-none select-none">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg"
                    >
                      {t("dashboard.ranges.week")}
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg"
                    >
                      {t("dashboard.ranges.month")}
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg"
                    >
                      {t("dashboard.ranges.ytd")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="text-xs text-gray-400">
                    {t("dashboard.financialYear.netProfitPreTax")}
                  </div>
                  <div
                    className="text-base font-semibold tabular-nums"
                    style={{ color: TITLE_COLOR }}
                  >
                    {fy.profit === null ? "--" : formatMoney(fy.profit)}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-400">{t("dashboard.financialYear.estimatedTaxPot")}</div>
                  <div
                    className="text-base font-semibold tabular-nums"
                    style={{ color: TITLE_COLOR }}
                  >
                    {!hasPositiveFYProfit ? "--" : formatMoney(fyTaxPot)}
                  </div>
                </div>
              </div>

              {fyEffectiveTaxRate !== null && hasPositiveFYProfit && (
                <p className="mt-2 text-[11px] text-gray-400 text-center">
                  {t("dashboard.financialYear.taxEstimateExplanation", {
                    rate: (fyEffectiveTaxRate * 100).toFixed(1),
                  })}
                </p>
              )}
            </div>
          </div>
        )}

        <div>
          <div
            className="text-[15px] font-semibold mb-2"
            style={{ color: TITLE_COLOR }}
          >
            {t("dashboard.quickLinks.title")}
          </div>
          <div className="text-gray-400 mb-5 text-[13px]">{t("dashboard.quickLinks.description")}</div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {filteredLinks.length === 0 ? (
              <div className="col-span-full text-center text-gray-400 py-12">
                {t("dashboard.quickLinks.noMatches")}
              </div>
            ) : (
              filteredLinks.map((item) => {
                const Icon = item.icon;

                return (
                  <button
                    key={item.route}
                    className={[
                      "flex flex-col items-center justify-center w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-5 group min-w-0",
                      pressTile,
                    ].join(" ")}
                    onClick={() =>
                      navigate(companyRoute(company_id, item.route))
                    }
                    title={item.label}
                  >
                    <div className="w-8 h-8 flex items-center justify-center shrink-0">
                      <Icon className="w-8 h-8 shrink-0 text-appleblue group-hover:text-appleblueD group-active:text-appleblueD transition" />
                    </div>

                    <div className="mt-2 w-full h-[44px] flex items-center justify-center min-w-0">
                      <div className="qlink-label font-medium text-[13px] w-full text-center leading-snug">
                        {item.label}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
